import React from "react";

/**
 * App-level ErrorBoundary — catches any unhandled render-time exception so a single
 * component bug doesn't silently blank the whole page (as happened with the
 * AdminDashboard `loadData` ReferenceError on Feb 19, 2026).
 *
 * Renders a friendly fallback with the error message + a reload button. Logs the
 * full error + componentStack to console.error for debugging.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    console.error("ErrorBoundary caught:", error, info?.componentStack);
  }

  handleReload = () => {
    window.location.href = "/";
  };

  render() {
    if (!this.state.hasError) return this.props.children;
    return (
      <div
        data-testid="app-error-boundary"
        className="min-h-screen flex items-center justify-center bg-background p-6"
      >
        <div className="max-w-lg w-full bg-card border rounded-xl p-8 shadow-lg text-center space-y-4">
          <h1 className="text-2xl font-bold text-red-600">Something went wrong</h1>
          <p className="text-sm text-muted-foreground">
            A page-level error prevented this view from rendering. We&apos;ve logged it.
          </p>
          {this.state.error?.message ? (
            <pre className="text-xs text-left bg-muted/40 rounded p-3 overflow-auto max-h-40">
              {this.state.error.message}
            </pre>
          ) : null}
          <button
            onClick={this.handleReload}
            className="px-4 py-2 rounded bg-primary text-primary-foreground hover:opacity-90"
            data-testid="error-boundary-reload"
          >
            Reload home
          </button>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
